// Project types ("FEATURE FILM", "COMMERCIAL") in the page's language.
//
// The server sends the type's name as casting.dbo.lstbreakdowntypes stores it
// (`bdtype`), or "Commercial" for a commercial, which has no type table. That
// table has a Spanish column (`bdType_3`) but no French one, and the shared
// clients.dbo.Localizations rows cover the types only partly. So the localized
// names live here (decided 2026-10-06: database columns if they exist, else the
// front end).
//
// Looked up by the name the server sends, ignoring case. `aliases` are the other
// spellings in that table — including the French-only rows ("Long métrage", …)
// that Quebec casting picks — so those read as "FEATURE FILM" on an English
// page too. A name not listed is shown as sent. projectTypes.test.ts holds the
// table's names as of 2026-10-06 and fails if one stops resolving.

import i18next from "i18next";

export interface IProjectTypeNames {
  en: string;
  fr: string;
  es: string;
  aliases?: string[];
}

export const PROJECT_TYPES: IProjectTypeNames[] = [
  // Film and television
  { en: "Feature Film", fr: "Long métrage", es: "Largometraje", aliases: ["Long métrage"] },
  { en: "Medium-Length Film", fr: "Moyen métrage", es: "Mediometraje", aliases: ["Moyen métrage"] },
  { en: "Short Film", fr: "Court métrage", es: "Cortometraje", aliases: ["Court métrage"] },
  { en: "MOW", fr: "Téléfilm", es: "Película para televisión" },
  { en: "Telemovie", fr: "Téléfilm", es: "Telefilme" },
  { en: "TV Feature", fr: "Long métrage pour la télévision", es: "Largometraje para televisión" },
  { en: "Cable Feature", fr: "Long métrage pour le câble", es: "Largometraje para cable" },
  { en: "TV Series", fr: "Série télé", es: "Serie de televisión", aliases: ["Séries TV"] },
  { en: "Pilot", fr: "Pilote", es: "Piloto" },
  { en: "Limited Series", fr: "Série limitée", es: "Serie limitada" },
  { en: "Mini-Series", fr: "Minisérie", es: "Miniserie" },
  { en: "Web Series", fr: "Websérie", es: "Serie web", aliases: ["Série web"] },
  { en: "Webisodes", fr: "Webépisodes", es: "Webisodios" },
  { en: "Animated Series", fr: "Série d'animation", es: "Serie de animación" },
  { en: "Animation", fr: "Animation", es: "Animación" },
  { en: "Docudrama", fr: "Docudrame", es: "Docudrama" },
  { en: "Documentary", fr: "Documentaire", es: "Documental", aliases: ["Documentaire"] },
  { en: "Magazine Show", fr: "Magazine", es: "Magacín", aliases: ["Magazine"] },
  { en: "Talk Show", fr: "Talk-show", es: "Programa de entrevistas" },
  { en: "Special", fr: "Émission spéciale", es: "Especial" },
  { en: "Film School", fr: "Film étudiant", es: "Escuela de cine" },
  { en: "Indie", fr: "Indépendant", es: "Independiente" },
  { en: "TIP (Toronto Indi Production)", fr: "TIP (Toronto Indi Production)", es: "TIP (Toronto Indi Production)" },
  { en: "Bravo!FACT", fr: "Bravo!FACT", es: "Bravo!FACT" },
  { en: "Direct To Video", fr: "Sortie directe en vidéo", es: "Directo a video" },
  { en: "Home Entertainment", fr: "Divertissement à domicile", es: "Entretenimiento en casa" },
  { en: "Music Video", fr: "Vidéoclip", es: "Videoclip", aliases: ["Music-Video", "Music Video / Filmclip", "Vidéoclip"] },
  { en: "Motion Capture", fr: "Capture de mouvement", es: "Captura de movimiento" },
  { en: "Table Read", fr: "Lecture à la table", es: "Lectura de guion" },

  // Commercial, corporate and print
  { en: "Commercial", fr: "Publicité", es: "Anuncio" },
  { en: "Infomercial", fr: "Infopub", es: "Infocomercial" },
  { en: "Industrial", fr: "Corporatif", es: "Corporativo" },
  { en: "Corporate Video", fr: "Vidéo corporative", es: "Video corporativo", aliases: ["Vidéo corporative"] },
  { en: "Educational", fr: "Éducatif", es: "Educativo" },
  { en: "Print", fr: "Imprimé", es: "Impresos", aliases: ["Imprimés"] },
  { en: "Fashion", fr: "Mode", es: "Moda" },
  { en: "Calendar", fr: "Calendrier", es: "Calendario" },
  { en: "Billboard", fr: "Affichage", es: "Valla publicitaria" },
  { en: "Brochure", fr: "Brochure", es: "Folleto" },
  { en: "Brochure / Catalog", fr: "Brochure / Catalogue", es: "Folleto / Catálogo" },
  { en: "POP", fr: "Point de vente", es: "Punto de venta" },
  { en: "Commercial Stills", fr: "Photos publicitaires", es: "Fotos publicitarias" },
  { en: "Film & TV Stills", fr: "Photos de plateau", es: "Fotos fijas de cine y TV" },

  // Digital
  { en: "Internet", fr: "Internet", es: "Internet" },
  { en: "Web", fr: "Web", es: "Web" },
  { en: "Online Video", fr: "Vidéo en ligne", es: "Video en línea" },
  { en: "New Media", fr: "Nouveaux médias", es: "Nuevos medios" },
  { en: "Multimedia", fr: "Multimédia", es: "Multimedia" },
  { en: "Video Game", fr: "Jeu vidéo", es: "Videojuego", aliases: ["Jeux vidéo"] },
  { en: "Direct To Talent", fr: "Directement aux talents", es: "Directo al talento" },
  { en: "Influencer Only", fr: "Influenceurs seulement", es: "Solo influencers" },
  { en: "AgentFind", fr: "AgentFind", es: "AgentFind", aliases: ["AgentFind (Old)"] },

  // Theatre
  { en: "Theatre", fr: "Théâtre", es: "Teatro" },
  { en: "Stage", fr: "Scène", es: "Escenario" },
  { en: "Play", fr: "Pièce de théâtre", es: "Obra de teatro" },
  { en: "Musical", fr: "Comédie musicale", es: "Musical" },
  { en: "Musical Theatre", fr: "Théâtre musical", es: "Teatro musical" },
  { en: "Tour", fr: "Tournée", es: "Gira" },
  { en: "Live", fr: "Spectacle en direct", es: "Espectáculo en vivo" },
  { en: "TYA", fr: "Théâtre jeune public", es: "Teatro infantil y juvenil" },
  { en: "Workshop", fr: "Atelier", es: "Taller" },

  // Voice
  { en: "Voice Over", fr: "Voix hors champ", es: "Voz en off", aliases: ["Voix hors champ"] },
  { en: "Film Narration", fr: "Narration de film", es: "Narración de película" },
  { en: "Audiobooks", fr: "Livres audio", es: "Audiolibros" },
  { en: "Radio Commercial", fr: "Publicité radio", es: "Anuncio de radio" },
  { en: "Radio Drama", fr: "Dramatique radio", es: "Radioteatro" },
  { en: "VO for on camera commercial", fr: "Voix hors champ pour publicité télé", es: "Voz en off para anuncio" },
  { en: "Dub Rythmo band", fr: "Doublage (bande rythmo)", es: "Doblaje con banda rítmica" },

  { en: "Other", fr: "Autre", es: "Otro", aliases: ["Autre"] },
];

const normalize = (name: string) => name.trim().toLowerCase();

const BY_NAME = new Map<string, IProjectTypeNames>();
PROJECT_TYPES.forEach((type) =>
  [type.en, ...(type.aliases || [])].forEach((name) => BY_NAME.set(normalize(name), type))
);

export const findProjectType = (name?: string | null): IProjectTypeNames | undefined =>
  name ? BY_NAME.get(normalize(name)) : undefined;

// The type in the page's language. English for every other language: the
// flow only runs in the languages it is complete in (flowLanguage.ts).
export const projectTypeLabel = (name?: string | null): string => {
  const raw = (name || "").trim();
  const type = findProjectType(raw);
  if (!type) return raw;
  const lang = i18next.language;
  return lang === "fr" || lang === "es" ? type[lang] : type.en;
};
